import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import format from 'date-fns/format';

import {
  answerBlock
} from 'actions';

import QuestionnaireType from 'common/questionnaires/QuestionnaireType';
import QuestionBlock from 'components/Questionnaires/QuestionBlock';
import Question from 'components/Questionnaires/Question';
import { QuestionAddressBlock, QuestionRcUsTaxResidentBlock, QuestionCanadianTaxResidentBlock } from 'components/Questionnaires/blocks';
import { QuestionTextField, QuestionDateDropdownField, QuestionDropDownField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion, isEmptyObject } from 'components/Questionnaires/validators';
import { rcTaxResidencesBlock } from 'common/questionnaires/definitions/blocks/blocks';

export const QuestionRcControllingPersonBlock = ({ block, onAnswer, preQualAnswers }) => {
  const dispatch = useDispatch();
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const [firstName, setFirstName] = useState({
    id: 'firstName',
    label: 'questionnaire.rc518.name.firstName.label',
    component: QuestionTextField,
    className: 'col-12 col-lg-9',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc518.name.firstName.errorMessage' }
      ]
    }
  });

  const [initials, setInitials] = useState({
    id: 'initials',
    label: 'questionnaire.rc518.name.initials.label',
    component: QuestionTextField,
    className: 'col-12 col-lg-3',
    visible: true,
  });

  const [lastName, setLastName] = useState({
    id: 'lastName',
    label: 'questionnaire.rc518.name.lastName.label',
    component: QuestionTextField,
    className: 'col-12 col-lg-12',
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc518.name.lastName.errorMessage' }
      ]
    }
  });

  const [birthDate, setBirthDate] = useState({
    id: 'birthDate',
    label: 'questionnaire.rc518.birth.date.label',
    component: QuestionDateDropdownField,
    componentProps: {
      minDate: format(moment().subtract(125, 'years').toDate(), 'dd-MM-yyyy'),
      maxDate: format(moment().subtract(1, 'days').toDate(), 'dd-MM-yyyy'),
      dateFormat: 'dd-MM-yyyy',
      showToolTip: true
    },
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc518.birth.errorMessage' }
      ]
    }
  });

  const [type, setType] = useState({
    id: 'type',
    label: 'questionnaire.rc518.controllingPerson.type.label',
    component: QuestionDropDownField,
    componentProps: {
      items: 'questionnaire.rc518.controllingPerson.type.items'
    },
    visible: true,
    validation: {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.rc518.controllingPerson.type.errorMessage' }
      ]
    }
  });

  const [address, setAddress] = useState({
    id: 'address',
    title: 'questionnaire.address',
    component: QuestionAddressBlock,
    info: {
      instructions: 'questionnaire.address.1.info'
    },
    className: 'p-0 no-border nested',
    linkBlock: true,
    visible: true,
  });

  const [mailingAddress, setMailingAddress] = useState({
    id: 'mailingAddress',
    title: 'questionnaire.mailingAddress',
    component: QuestionAddressBlock,
    componentProps: {
      line1Label: 'questionnaire.mailingAddress.1.label',
      optional: true
    },
    info: {
      instructions: 'questionnaire.mailingAddress.1.info'
    },
    className: 'p-0 no-border nested',
    linkBlock: true,
    visible: true
  });

  const [cpCanadianTaxResident, setCpCanadianTaxResident] = useState({
    id: 'cpCanadianTaxResident',
    component: QuestionCanadianTaxResidentBlock,
    title: 'questionnaire.rc518.canadianTaxResident',
    visible: true,
    className: 'p-0 no-border nested',
    linkBlock: true,
  });

  const [cpUsTaxResident, setCpUsTaxResident] = useState({
    id: 'cpUsTaxResident',
    title: 'questionnaire.rc518.usTaxResident',
    component: QuestionRcUsTaxResidentBlock,
    visible: true,
    componentProps: {
      tinType: 'SSN'
    },
    className: 'p-0 no-border nested',
    linkBlock: true,
  });

  const [taxResidences, setTaxResidences] = useState({
    ...rcTaxResidencesBlock(),
    isCollapsed: false,
    className: 'p-0 no-border nested',
    linkBlock: true
  });

  const isValid = () => {
    if (firstName.answer === undefined
      || lastName.answer === undefined
      || birthDate.answer === undefined
      || type.answer === undefined
      || address.answer === undefined
      || cpCanadianTaxResident.answer === undefined
      || cpUsTaxResident.answer === undefined
      || taxResidences.answer === undefined) {
      return undefined;
    }

    return firstName.isValid
      && lastName.isValid
      && birthDate.isValid
      && type.isValid
      && (!isEmptyObject(address.answer) && address.isValid)
      && (!mailingAddress.answer || mailingAddress.isValid)
      && (!cpCanadianTaxResident.answer || cpCanadianTaxResident.isValid)
      && (!cpUsTaxResident.answer || cpUsTaxResident.isValid)
      && (!taxResidences.answer || taxResidences.answer.length > 0);
  };

  const onBlockAnswer = (b, a, v) => {
    switch (b.id) {
      case 'address': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setAddress(newBlock);
        setAnswer({ ...answer, address: a });
        break;
      }
      case 'mailingAddress': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setMailingAddress(newBlock);
        setAnswer({ ...answer, mailingAddress: a });
        break;
      }
      case 'taxResidences': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setTaxResidences(newBlock);
        setAnswer({ ...answer, taxResidences: a });
        break;
      }
      case 'cpCanadianTaxResident': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setCpCanadianTaxResident(newBlock);
        setAnswer({ ...answer, cpCanadianTaxResident: a });
        break;
      }
      case 'cpUsTaxResident': {
        const newBlock = { ...b, ...{ answer: a, isValid: v } };
        setCpUsTaxResident(newBlock);
        setAnswer({ ...answer, cpUsTaxResident: a });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid()));
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'firstName': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setFirstName(newQuestion);
        setAnswer({ ...answer, firstName: value });
        break;
      }
      case 'initials': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setInitials(newQuestion);
        setAnswer({ ...answer, initials: value });
        break;
      }
      case 'lastName': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setLastName(newQuestion);
        setAnswer({ ...answer, lastName: value });
        break;
      }
      case 'birthDate': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setBirthDate(newQuestion);
        setAnswer({ ...answer, birthDate: value });
        break;
      }
      case 'type': {
        const newQuestion = { ...q, answer: value };
        validateQuestion(newQuestion);
        setType(newQuestion);
        setAnswer({ ...answer, type: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (!answer?.firstName) {
        onQuestionChange({ ...firstName, isForceEdited: true }, '');
      }
      if (!answer?.lastName) {
        onQuestionChange({ ...lastName, isForceEdited: true }, '');
      }
      if (!answer?.birthDate) {
        onQuestionChange({ ...birthDate, isForceEdited: true }, '');
      }
      if (!answer?.type) {
        onQuestionChange({ ...type, isForceEdited: true }, '');
      }
      if (!answer.address || isEmptyObject(answer.address)) {
        const newBlock = { ...address, ...{ isForceEdited: true } };
        setAddress(newBlock);
      }
      if (answer.mailingAddress) {
        const newBlock = { ...mailingAddress, ...{ isForceEdited: true } };
        setMailingAddress(newBlock);
      }
      if (!answer.cpCanadianTaxResident || isEmptyObject(cpCanadianTaxResident)) {
        const newBlock = { ...cpCanadianTaxResident, ...{ isForceEdited: true } };
        setCpCanadianTaxResident(newBlock);
      }
      if (!answer.cpUsTaxResident || isEmptyObject(cpUsTaxResident)) {
        const newBlock = { ...cpUsTaxResident, ...{ isForceEdited: true } };
        setCpUsTaxResident(newBlock);
      }
      if (!answer.taxResidences || answer.taxResidences.length > 0) {
        const newBlock = { ...taxResidences, ...{ isForceEdited: true } };
        setTaxResidences(newBlock);
      }
    }

    if (block.isForceEdited === undefined) {
      if (mailingAddress.isForceEdited === true) {
        const newBlock = { ...mailingAddress, isForceEdited: undefined };
        setMailingAddress(newBlock);
      }
    }
  }, [block.isForceEdited]);

  return (<div className="questions row cp-section">
    <Question key={ firstName.id } blockId={ block.id } question={ firstName } { ...firstName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ initials.id } blockId={ block.id } question={ initials } { ...initials.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ lastName.id } blockId={ block.id } question={ lastName } { ...lastName.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ birthDate.id } blockId={ block.id } question={ birthDate } { ...birthDate.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ type.id } blockId={ block.id } question={ type } { ...type.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <div className="col-12">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ address.id }` } block={ address } onAnswer={ onBlockAnswer } />
    </div>
    <div className="col-12">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ mailingAddress.id }` } block={ mailingAddress } onAnswer={ onBlockAnswer } />
    </div>
    <div className="col-12">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ cpCanadianTaxResident.id }` } block={ cpCanadianTaxResident } onAnswer={ onBlockAnswer } />
    </div>
    <div className="col-12">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ cpUsTaxResident.id }` } block={ cpUsTaxResident } onAnswer={ onBlockAnswer } />
    </div>
    <div className="col-12 pb-4">
      <QuestionBlock questionnaireType={ QuestionnaireType.Form } key={ `${ block.id }-${ taxResidences.id }` } block={ taxResidences } onAnswer={ onBlockAnswer } />
    </div>
  </div>);
};

QuestionRcControllingPersonBlock.displayName = 'QuestionRcControllingPersonBlock';

QuestionRcControllingPersonBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape()
};

QuestionRcControllingPersonBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionRcControllingPersonBlock;
